/**
 * Lot Focus Handlers for Native Interactive Site Map (ISM)
 *
 * Owns DOM lookup and geometry calculations for selected-lot camera focus. This module has no
 * Redux, map-camera, or plugin lifecycle responsibilities.
 *
 * @module nativeInteractiveSiteMap/handlers/lotFocusHandlers
 */
import LayerTypes from '../enums/layerTypes';

const LOT_POLYGONS_LAYER_SELECTOR = `[data-layer-type="${LayerTypes.LotPolygons}"]`;
const MOBILE_DRAWER_RESERVATION_RATIO = 0.5;

function createLotFocusHandlers() {
  // A lot id is shared by both the LotPolygons shape and the LotNumbers label, so limit the
  // matching element to the polygon layer.
  function findLotPolygonElement(svgElement, lotId) {
    return Array.from(svgElement.querySelectorAll(`[id="${CSS.escape(lotId)}"]`))
      .find((element) => element.closest(LOT_POLYGONS_LAYER_SELECTOR));
  }

  function getFocusElements(svgElement, rootElement, lotId) {
    return {
      lotElement: findLotPolygonElement(svgElement, lotId),
      mapElement: rootElement.querySelector('[data-map-element]'),
    };
  }

  // Returns the lot center as a pixel offset from the map div center, which is the coordinate
  // system expected by the map projection in this environment.
  function getLotOffsetFromMapCenter(lotElement, mapElement) {
    const lotRect = lotElement.getBoundingClientRect();
    const mapRect = mapElement.getBoundingClientRect();

    return {
      x: lotRect.left + lotRect.width / 2 - (mapRect.left + mapRect.width / 2),
      y: lotRect.top + lotRect.height / 2 - (mapRect.top + mapRect.height / 2),
    };
  }

  // Reserve the lower half of the map for the mobile drawer without coupling to its DOM or animation.
  function getMobileDrawerReservedHeight(mapElement) {
    return mapElement.getBoundingClientRect().height * MOBILE_DRAWER_RESERVATION_RATIO;
  }

  return { getFocusElements, getLotOffsetFromMapCenter, getMobileDrawerReservedHeight };
}

export default createLotFocusHandlers;